'use client';

import {
    useReviewsPageDataContext,
    useReviewsPageQueryContext,
    useReviewsPageTransportContext,
} from '@/context/reviews-page.context';
import { useAuth } from '@/context/auth.context';
import { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { useReviewsClientPagePagination } from '@/components/reviews-client-page/use-reviews-client-page-pagination';
import { useReviewsClientPageRowsFit } from '@/components/reviews-client-page/use-reviews-client-page-rows-fit';
import { useReviewsClientPageQueryActions } from '@/components/reviews-client-page/use-reviews-client-page-query-actions';
import { useReviewsClientPageRefetchEffect } from '@/components/reviews-client-page/use-reviews-client-page-refetch.effect';
import {
    buildReviewsClientPageControllerBindings,
    type ReviewsClientPageControllerBindings,
} from '@/components/reviews-client-page/reviews-client-page-controller-bindings.builder';
import type { ActiveTable } from '@/components/reviews-client-page/reviews-client-page.types';
import {
    DEFAULT_REVIEWS_PAGE,
    readStoredReviewFilters,
    readStoredReviewsOnPage,
    readStoredReviewSort,
    readStoredReviewPage,
    writeStoredReviewFilters,
    writeStoredReviewsOnPage,
    writeStoredReviewPage,
    writeStoredReviewSort,
} from '@/context/reviews-page-transport.helpers';

export type { ActiveTable } from '@/components/reviews-client-page/reviews-client-page.types';

export const useReviewsClientPageController = (): ReviewsClientPageControllerBindings => {
    const { total, products, brandGroups } = useReviewsPageDataContext();
    const { refetchReviewsAndProducts, sort, setSort, filters, setFilters, reviewsOnPage, setReviewsOnPage } =
        useReviewsPageQueryContext();
    const { refetchReviews } = useReviewsPageTransportContext();
    const { clientId } = useAuth();
    const [reviewsPage, setReviewsPageState] = useState<number>(DEFAULT_REVIEWS_PAGE);
    const [isStorageHydrated, setIsStorageHydrated] = useState(false);

    useLayoutEffect(() => {
        setIsStorageHydrated(false);
        setReviewsPageState(readStoredReviewPage(clientId));
        setFilters(readStoredReviewFilters(clientId));
        setSort(readStoredReviewSort(clientId));
        void setReviewsOnPage(readStoredReviewsOnPage(clientId));
        setIsStorageHydrated(true);
    }, [clientId, setFilters, setReviewsOnPage, setSort]);

    useEffect(() => {
        if (!isStorageHydrated) {
            return;
        }

        writeStoredReviewFilters(clientId, filters);
    }, [clientId, filters, isStorageHydrated]);

    useEffect(() => {
        if (!isStorageHydrated) {
            return;
        }

        writeStoredReviewSort(clientId, sort);
    }, [clientId, isStorageHydrated, sort.sortKey, sort.sortOrder]);

    useEffect(() => {
        if (!isStorageHydrated) {
            return;
        }

        writeStoredReviewsOnPage(clientId, reviewsOnPage);
    }, [clientId, isStorageHydrated, reviewsOnPage]);

    const setReviewsPage = useCallback(
        (nextPage: number) => {
            const normalizedPage = writeStoredReviewPage(clientId, nextPage);
            setReviewsPageState(normalizedPage);
        },
        [clientId],
    );

    useEffect(() => {
        const maxPage = Math.max(1, Math.ceil(total / reviewsOnPage));

        if (reviewsPage > maxPage) {
            setReviewsPage(maxPage);
        }
    }, [reviewsOnPage, reviewsPage, setReviewsPage, total]);

    const [activeTable, setActiveTable] = useState<ActiveTable>('reviews');
    const {
        productsPage,
        setProductsPage,
        productsOnPage,
        setProductsOnPage,
        productsPageCount,
        setProductsPageCount,
        productsTotalRows,
        setProductsTotalRows,
        brandsPage,
        setBrandsPage,
        brandsOnPage,
        setBrandsOnPage,
        brandsPageCount,
        setBrandsPageCount,
        brandsTotalRows,
        setBrandsTotalRows,
        onProductsOnPageUpdate,
        onBrandsOnPageUpdate,
    } = useReviewsClientPagePagination({
        clientId,
        productsLength: products.length,
        brandGroupsLength: brandGroups.length,
    });

    const { rowsPerPagePresets, scrollContainerRef, topControlsContainerRef } = useReviewsClientPageRowsFit({
        activeTable,
        reviewsOnPage,
        setReviewsOnPage,
        productsOnPage,
        setProductsOnPage,
        setProductsPage,
        brandsOnPage,
        setBrandsOnPage,
        setBrandsPage,
    });

    const onReviewsOnPageUpdate = useCallback(
        async (count: number) => {
            setReviewsPage(1);
            await setReviewsOnPage(count);
        },
        [setReviewsOnPage, setReviewsPage],
    );

    const { onSortChanged, updateFilters, setPage } = useReviewsClientPageQueryActions({
        setSort,
        setFilters,
        setReviewsPage,
    });

    useReviewsClientPageRefetchEffect({
        page: reviewsPage,
        reviewsOnPage,
        sort,
        filters,
        refetchReviews: refetchReviews || refetchReviewsAndProducts,
    });

    return buildReviewsClientPageControllerBindings({
        data: {
            total,
            products,
            brandGroups,
            filters,
        },
        pagination: {
            reviewsPage,
            reviewsOnPage,
            setReviewsOnPage: onReviewsOnPageUpdate,
            activeTable,
            setActiveTable,
            productsPage,
            setProductsPage,
            productsOnPage,
            productsPageCount,
            setProductsPageCount,
            productsTotalRows,
            setProductsTotalRows,
            brandsPage,
            setBrandsPage,
            brandsOnPage,
            brandsPageCount,
            setBrandsPageCount,
            brandsTotalRows,
            setBrandsTotalRows,
            onProductsOnPageUpdate,
            onBrandsOnPageUpdate,
        },
        layout: {
            rowsPerPagePresets,
            scrollContainerRef,
            topControlsContainerRef,
        },
        queryActions: {
            onSortChanged,
            updateFilters,
            setPage,
        },
    });
};

export type ReviewsClientPageController = ReviewsClientPageControllerBindings;
